import {
  type CostEstimate,
  clientCostEstimateShowQueryKey,
  useClientCostEstimateSendToFamilyMutation,
} from '@cartwheel/api/v1'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '../button'
import { useToast } from '../toast/use-toast'

interface SendToFamilyButtonProps {
  clientId: string
  estimate: CostEstimate
}

export function SendToFamilyButton({ clientId, estimate }: SendToFamilyButtonProps) {
  const toast = useToast()
  const queryClient = useQueryClient()

  const mutation = useClientCostEstimateSendToFamilyMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: clientCostEstimateShowQueryKey({ path: { client_id: clientId } }),
      })
      toast.success({ description: 'Cost estimate sent to family' })
    },
    onError: () => {
      toast.error({ description: 'Failed to send cost estimate' })
    },
  })

  const handleClick = () => {
    // Parent only renders this button for a saved estimate, so estimate.id is non-null in practice.
    if (!estimate.id) return
    if (!window.confirm('Are you sure?')) return
    mutation.mutate({ path: { client_id: clientId, id: estimate.id } })
  }

  return (
    <div className="mt-6 flex justify-end">
      <div className="flex flex-col items-end">
        <Button
          variant="primary"
          type="button"
          disabled={!estimate.valid_for_send || mutation.isPending}
          onClick={handleClick}
        >
          {mutation.isPending ? 'Sending…' : 'Send estimate to family'}
        </Button>
        {!estimate.valid_for_send && (
          <span className="mt-2 text-sm text-red-800">
            This estimate needs manual review. Please edit prior to sending.
          </span>
        )}
      </div>
    </div>
  )
}
